import type { PlatformTheme } from "../api/types";

// Platform visual identity: the Bifrost theme set, the logo-to-theme mapping,
// and the client-side twin of keycloak-api's PlatformIdentity validation.

// The Bifrost color themes (bifrost design/colors: Teal, Purple, Pink,
// Yellow). Neutral is not an accent: it is the gray theme (bf-theme-gray).
export const PLATFORM_THEMES: { value: PlatformTheme; label: string }[] = [
  { value: "teal", label: "Teal" },
  { value: "purple", label: "Purple" },
  { value: "pink", label: "Pink" },
  { value: "yellow", label: "Yellow" },
];

export type ThemeChoice = PlatformTheme | "neutral";

// The theme class a platform wears. A themed choice wears its accent;
// neutral wears the gray theme, not the Portal's default teal.
export function themeClass(
  theme: PlatformTheme | ThemeChoice | null | undefined,
): string {
  if (!theme || theme === "neutral") return "bf-theme-gray";
  return `bf-theme-${theme}`;
}

export function themeLabel(theme: PlatformTheme | null | undefined): string {
  if (!theme) return "Neutral";
  return PLATFORM_THEMES.find((t) => t.value === theme)?.label ?? theme;
}

// The theme a builtin logo carries. A builtin logo cannot be neutral, so the
// theme follows the logo (bifrost design/theme: "use the theme matching your
// app logo"). Derived from each logo's dominant color; adjust per design
// review. A logo missing here leaves the theme to the user.
export const LOGO_THEME_MAP: Record<string, PlatformTheme> = {
  "365-manager": "purple",
  "ai-compute": "teal",
  "application-platform-manager": "purple",
  applications: "purple",
  "automation-as-a-service": "purple",
  bifrost: "pink",
  "company-manager": "teal",
  conference: "pink",
  "consultancy-billing": "purple",
  "data-pipelines": "purple",
  "developer-platform": "pink",
  "digital-signage": "purple",
  "edge-infrastructure": "purple",
  esign: "teal",
  finance: "purple",
  "high-performance-vdi": "teal",
  "identity-manager": "yellow",
  "industrial-edge": "teal",
  insight: "pink",
  "intility-client-health": "teal",
  "intility-client-integrity": "teal",
  "intility-cloud-email-relay": "yellow",
  "intility-docs": "purple",
  "intility-fiber": "pink",
  "intility-flow": "teal",
  "intility-gpt": "teal",
  "intility-gym": "teal",
  "intility-incloud": "teal",
  "intility-inference": "pink",
  "intility-meet": "teal",
  "intility-mobility-management": "pink",
  "intility-secure-filemover": "purple",
  "intility-sim": "purple",
  "intility-sms": "purple",
  "intility-transcribe": "teal",
  "intility-tv": "pink",
  inventory: "pink",
  "license-manager": "yellow",
  "make-me-admin": "teal",
  "managed-guest-print": "yellow",
  "managed-linux-client": "pink",
  "managed-meeting-room": "teal",
  "managed-print-services": "teal",
  messages: "pink",
  monitoring: "pink",
  "my-intility": "teal",
  network: "purple",
  notify: "pink",
  npc: "teal",
  onboard: "teal",
  "operation-centers": "pink",
  "operations-manager": "teal",
  pcm: "purple",
  "platform-manager": "yellow",
  portal: "teal",
  "printer-as-a-service": "purple",
  "procurement-manager": "pink",
  "property-infrastructure": "teal",
  publish: "purple",
  roadmap: "teal",
  seating: "pink",
  "secure-admin-management": "teal",
  "secure-print": "pink",
  security: "pink",
  "service-catalog": "yellow",
  "setup-portal": "teal",
  "social-zones": "yellow",
  support: "teal",
  sustainability: "teal",
  "teams-room": "teal",
  toolkit: "pink",
  tools: "teal",
  webshop: "pink",
  "workload-manager": "purple",
  "workplace-manager": "pink",
  yggdrasil: "purple",
};

export function builtinTheme(logoKey: string): PlatformTheme | undefined {
  return LOGO_THEME_MAP[logoKey];
}

// The client-side twin of keycloak-api's limits (PlatformIdentity). The
// backend enforces them again; these make the upload form fail fast.
export const LOGO_MIME_TYPES = ["image/png", "image/jpeg", "image/webp"];

export const MAX_LOGO_DATA_LENGTH = 350_000;

export const LOGO_ACCEPT = ".png,.jpg,.jpeg,.webp";

// Reads a picked logo file into a base64 data URI, rejecting anything the
// backend would refuse: a wrong format or too large an image.
export async function readLogoFile(file: File): Promise<string> {
  if (!LOGO_MIME_TYPES.includes(file.type)) {
    throw new Error(
      `Unsupported logo format "${file.type}". Use PNG, JPG or WebP.`,
    );
  }
  const dataUri = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error("Could not read the file."));
    reader.readAsDataURL(file);
  });
  if (dataUri.length > MAX_LOGO_DATA_LENGTH) {
    throw new Error("The logo is too large. Use an image under about 256 KiB.");
  }
  return dataUri;
}
